import { useIntlLocale } from '../intl-locale';
import { formatCopy } from '@/i18n/ns';

/** Date and time in the workspace's locale; an invalid value is "unknown", never "now". */
export function useTimeLabel(): (value: string | null | undefined) => string | null {
  const locale = useIntlLocale();
  return (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isFinite(date.getTime())
      ? new Intl.DateTimeFormat(locale, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date)
      : null;
  };
}

/**
 * How long ago, as the language says it ("3 hours ago", "yesterday"); a week or older reads as its date. An invalid
 * value is "unknown", never "now".
 */
export function relativeTimeLabel(
  value: string | null | undefined,
  locale: string,
  now = Date.now(),
): string | null {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (!Number.isFinite(then)) return null;
  const elapsed = Math.max(0, now - then);
  const MINUTE = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  try {
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (elapsed < MINUTE) return relative.format(0, 'second');
    if (elapsed < HOUR) return relative.format(-Math.floor(elapsed / MINUTE), 'minute');
    if (elapsed < DAY) return relative.format(-Math.floor(elapsed / HOUR), 'hour');
    if (elapsed < 7 * DAY) return relative.format(-Math.floor(elapsed / DAY), 'day');
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(then);
  } catch {
    return null;
  }
}
export function useRelativeTimeLabel(): (value: string | null | undefined) => string | null {
  const locale = useIntlLocale();
  return (value) => relativeTimeLabel(value, locale);
}

export function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A short mono tile label for a system ("Shopify" -> "Sh"). Presentation only. */
export function tileText(name: string | null): string {
  const clean = (name ?? '').trim();
  return clean ? clean.slice(0, 2) : '·';
}

export { formatCopy };
